import { useEffect, useState, useCallback } from "react";
import {
    FiTruck,
    FiPlus,
    FiSearch,
    FiUsers,
    FiMapPin,
    FiEdit2,
    FiTrash2,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import transportService from "../../services/transportService";
import studentService from "../../services/studentService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const DEFAULT_SLABS = [
    { minKm: 0, maxKm: 10, label: "0 to 10 Kms", monthlyFee: 1300 },
    { minKm: 11, maxKm: 15, label: "11 to 15 Kms", monthlyFee: 1500 },
    { minKm: 16, maxKm: 20, label: "16 to 20 Kms", monthlyFee: 2000 },
];

export default function TransportManagement() {
    const { academicYear, selectedCollegeId, collegeData, collegeName } = useApp();

    const [activeTab, setActiveTab] = useState("passengers"); // "passengers" | "routes"

    // Routes state
    const [routes, setRoutes] = useState([]);
    const [loadingRoutes, setLoadingRoutes] = useState(true);

    // Passengers state
    const [passengers, setPassengers] = useState([]);
    const [loadingPassengers, setLoadingPassengers] = useState(true);
    const [searchPassenger, setSearchPassenger] = useState("");
    const [routeFilter, setRouteFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("Active");

    // Allotment Modal
    const [showAllotModal, setShowAllotModal] = useState(false);
    const [allotForm, setAllotForm] = useState({
        student: "",
        route: "",
        stopName: "",
        distanceKm: "",
        monthlyFee: "",
        startDate: new Date().toISOString().split("T")[0],
        remarks: "",
    });
    const [studentSearch, setStudentSearch] = useState("");
    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchingStudents, setSearchingStudents] = useState(false);
    const [submittingAllot, setSubmittingAllot] = useState(false);

    // Route Create/Edit Modal
    const [showRouteModal, setShowRouteModal] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [routeForm, setRouteForm] = useState({
        routeName: "",
        busNumber: "",
        driverName: "",
        driverPhone: "",
        capacity: 50,
        slabs: DEFAULT_SLABS,
        stops: [{ stopName: "", distanceKm: 0, pickupTime: "", dropTime: "" }],
    });
    const [submittingRoute, setSubmittingRoute] = useState(false);

    // Collect Fee Modal
    const [payPassengerTarget, setPayPassengerTarget] = useState(null);
    const [passengerMonthsPaid, setPassengerMonthsPaid] = useState(1);
    const [passengerPayForm, setPassengerPayForm] = useState({
        paymentMode: "Cash",
        receiptNo: "",
        transactionRef: "",
        paymentDate: new Date().toLocaleDateString("en-IN"),
        remarks: "",
    });
    const [submittingPassengerPay, setSubmittingPassengerPay] = useState(false);

    // ── Fetch Routes ──────────────────────────────────────────────────────────
    const fetchRoutes = useCallback(async () => {
        try {
            setLoadingRoutes(true);
            const params = {};
            if (selectedCollegeId && selectedCollegeId !== "all") {
                params.college = selectedCollegeId;
            }
            const res = await transportService.getRoutes(params);
            const list = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                    ? res.data.data
                    : Array.isArray(res)
                        ? res
                        : [];
            setRoutes(list);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to load routes");
        } finally {
            setLoadingRoutes(false);
        }
    }, [selectedCollegeId]);

    // ── Fetch Passengers ──────────────────────────────────────────────────────
    const fetchPassengers = useCallback(async () => {
        try {
            setLoadingPassengers(true);
            const params = {
                academicYear: academicYear?._id,
                route: routeFilter !== "ALL" ? routeFilter : undefined,
                status: statusFilter !== "ALL" ? statusFilter : undefined,
                search: searchPassenger,
            };
            if (selectedCollegeId && selectedCollegeId !== "all") {
                params.college = selectedCollegeId;
            }
            const res = await transportService.getPassengers(params);
            const list = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                    ? res.data.data
                    : Array.isArray(res)
                        ? res
                        : [];
            setPassengers(list);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to load passengers");
        } finally {
            setLoadingPassengers(false);
        }
    }, [academicYear, selectedCollegeId, routeFilter, statusFilter, searchPassenger]);

    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    useEffect(() => {
        fetchPassengers();
    }, [fetchPassengers]);

    // ── Student Search for Bus Allotment ───────────────────────────────────────
    useEffect(() => {
        if (!studentSearch || !studentSearch.trim()) {
            setStudentsList([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                setSearchingStudents(true);
                const res = await studentService.getAll({
                    search: studentSearch.trim(),
                    status: "Active",
                    limit: 20,
                });
                const list = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.students)
                        ? res.data.students
                        : (Array.isArray(res.data?.data) ? res.data.data : []);
                setStudentsList(list);
            } catch (e) {
                setStudentsList([]);
            } finally {
                setSearchingStudents(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [studentSearch]);

    // ── Auto-Calculate Fee from Selected Distance ─────────────────────────────
    const handleDistanceChange = (kmVal) => {
        const km = Number(kmVal);
        let calculatedFee = 1300;
        let selectedRouteDoc = routes.find((r) => r._id === allotForm.route);

        if (selectedRouteDoc && selectedRouteDoc.slabs?.length > 0) {
            const slab = selectedRouteDoc.slabs.find((s) => km >= s.minKm && km <= s.maxKm);
            if (slab) calculatedFee = slab.monthlyFee;
        } else {
            if (km <= 10) calculatedFee = 1300;
            else if (km <= 15) calculatedFee = 1500;
            else calculatedFee = 2000;
        }

        setAllotForm((prev) => ({
            ...prev,
            distanceKm: kmVal,
            monthlyFee: calculatedFee,
        }));
    };

    // ── Handle Stop Selected from Route Stops ─────────────────────────────────
    const handleStopSelected = (stopName) => {
        const selectedRouteDoc = routes.find((r) => r._id === allotForm.route);
        if (!selectedRouteDoc) {
            setAllotForm((prev) => ({ ...prev, stopName }));
            return;
        }
        const matchedStop = selectedRouteDoc.stops?.find((s) => s.stopName === stopName);
        if (matchedStop) {
            handleDistanceChange(matchedStop.distanceKm);
            setAllotForm((prev) => ({ ...prev, stopName }));
        } else {
            setAllotForm((prev) => ({ ...prev, stopName }));
        }
    };

    // ── Submit Bus Allotment ──────────────────────────────────────────────────
    const handleAllotSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) {
            toast.error("Please search and select a student");
            return;
        }
        if (!allotForm.route || !allotForm.stopName || !allotForm.distanceKm) {
            toast.error("Route, Stop Name, and Distance are required");
            return;
        }

        try {
            setSubmittingAllot(true);
            await transportService.subscribe({
                student: selectedStudent._id,
                academicYear: academicYear?._id,
                route: allotForm.route,
                stopName: allotForm.stopName,
                distanceKm: Number(allotForm.distanceKm),
                monthlyFee: Number(allotForm.monthlyFee),
                startDate: allotForm.startDate,
                remarks: allotForm.remarks,
            });
            toast.success(`Bus facility allotted to ${selectedStudent.fullName}`);
            setShowAllotModal(false);
            setSelectedStudent(null);
            setStudentSearch("");
            setAllotForm({
                student: "",
                route: "",
                stopName: "",
                distanceKm: "",
                monthlyFee: "",
                startDate: new Date().toISOString().split("T")[0],
                remarks: "",
            });
            fetchPassengers();
            fetchRoutes();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to allot bus facility");
        } finally {
            setSubmittingAllot(false);
        }
    };

    // ── Cancel/Discontinue Bus Allotment ──────────────────────────────────────
    const handleCancelAllotment = async (p) => {
        const reason = window.prompt(`Enter reason for discontinuing bus facility for ${p.student?.fullName}:`);
        if (reason === null) return;

        try {
            await transportService.cancel(p._id, { remarks: reason });
            toast.success("Bus subscription discontinued");
            fetchPassengers();
            fetchRoutes();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to cancel allotment");
        }
    };

    const handleCollectPassengerFee = async (e) => {
        e.preventDefault();
        if (!payPassengerTarget?._id) return;
        try {
            setSubmittingPassengerPay(true);
            const numMonths = Number(passengerMonthsPaid) || 1;
            const res = await transportService.collectFee(payPassengerTarget._id, {
                monthsPaid: numMonths,
                amountPaid: numMonths * Number(payPassengerTarget.monthlyFee || 0),
                paymentMode: passengerPayForm.paymentMode,
                paymentDate: passengerPayForm.paymentDate,
                receiptNo: passengerPayForm.receiptNo,
                transactionRef: passengerPayForm.transactionRef,
                remarks: passengerPayForm.remarks,
            });
            toast.success(res.data?.message || "Bus fee collected successfully");
            setPayPassengerTarget(null);
            fetchPassengers();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to collect bus fee");
        } finally {
            setSubmittingPassengerPay(false);
        }
    };

    // ── Route Form Actions ────────────────────────────────────────────────────
    const openCreateRoute = () => {
        setEditingRoute(null);
        setRouteForm({
            routeName: "",
            busNumber: "",
            driverName: "",
            driverPhone: "",
            capacity: 50,
            slabs: DEFAULT_SLABS,
            stops: [{ stopName: "", distanceKm: 0, pickupTime: "", dropTime: "" }],
        });
        setShowRouteModal(true);
    };

    const openEditRoute = (r) => {
        setEditingRoute(r);
        setRouteForm({
            routeName: r.routeName,
            busNumber: r.busNumber || "",
            driverName: r.driverName || "",
            driverPhone: r.driverPhone || "",
            capacity: r.capacity || 50,
            slabs: r.slabs && r.slabs.length > 0 ? r.slabs : DEFAULT_SLABS,
            stops: r.stops && r.stops.length > 0 ? r.stops : [{ stopName: "", distanceKm: 0, pickupTime: "", dropTime: "" }],
        });
        setShowRouteModal(true);
    };

    const handleRouteSubmit = async (e) => {
        e.preventDefault();
        if (!routeForm.routeName.trim()) {
            toast.error("Route name is required");
            return;
        }

        try {
            setSubmittingRoute(true);
            if (editingRoute) {
                await transportService.updateRoute(editingRoute._id, routeForm);
                toast.success("Route updated successfully");
            } else {
                await transportService.createRoute(routeForm);
                toast.success("Route created successfully");
                setActiveTab("routes");
            }
            setShowRouteModal(false);
            fetchRoutes();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to save route");
        } finally {
            setSubmittingRoute(false);
        }
    };

    const handleDeleteRoute = async (r) => {
        if (!window.confirm(`Delete route "${r.routeName}"?`)) return;
        try {
            await transportService.deleteRoute(r._id);
            toast.success("Route deleted");
            fetchRoutes();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to delete route");
        }
    };

    // Helper for slab editing in modal
    const updateSlabFee = (index, fee) => {
        const newSlabs = [...routeForm.slabs];
        newSlabs[index].monthlyFee = Number(fee);
        setRouteForm({ ...routeForm, slabs: newSlabs });
    };

    // Helper for stops editing in modal
    const addStopRow = () => {
        setRouteForm({
            ...routeForm,
            stops: [...routeForm.stops, { stopName: "", distanceKm: 0, pickupTime: "", dropTime: "" }],
        });
    };

    const removeStopRow = (index) => {
        const newStops = routeForm.stops.filter((_, i) => i !== index);
        setRouteForm({ ...routeForm, stops: newStops });
    };

    const updateStopField = (index, field, value) => {
        const newStops = [...routeForm.stops];
        newStops[index][field] = field === "distanceKm" ? Number(value) : value;
        setRouteForm({ ...routeForm, stops: newStops });
    };

    return (
        <div className="page-wrapper" style={{ padding: "1.5rem" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, color: "var(--text)" }}>
                            Bus &amp; Transport Management
                        </h1>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: !selectedCollegeId || selectedCollegeId === "all" ? "#f1f5f9" : "#e0f2fe",
                            color: !selectedCollegeId || selectedCollegeId === "all" ? "#475569" : "#0369a1",
                            border: "1px solid " + (!selectedCollegeId || selectedCollegeId === "all" ? "#cbd5e1" : "#bae6fd"),
                        }}>
                            {!selectedCollegeId || selectedCollegeId === "all" ? "🌐 All Campuses" : `🏢 ${collegeData?.name || collegeName}`}
                        </span>
                    </div>
                    <p style={{ color: "var(--text-muted)", margin: "0.25rem 0 0", fontSize: "0.875rem" }}>
                        Manage bus routes, kilometer distance slabs, and student bus pass allotments
                    </p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        onClick={openCreateRoute}
                        className="btn btn-outline"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 600 }}
                    >
                        <FiTruck size={15} /> Add Bus Route
                    </button>
                    <button
                        onClick={() => setShowAllotModal(true)}
                        className="btn btn-primary"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}
                    >
                        <FiPlus size={16} /> Allot Bus Pass
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div style={{
                display: "flex",
                borderBottom: "1px solid var(--border)",
                marginBottom: "1.5rem",
                gap: "1.5rem",
            }}>
                <button
                    type="button"
                    onClick={() => setActiveTab("passengers")}
                    style={{
                        background: "none",
                        border: "none",
                        borderBottom: activeTab === "passengers" ? "2px solid #003b67" : "2px solid transparent",
                        color: activeTab === "passengers" ? "#003b67" : "var(--text-muted)",
                        fontWeight: activeTab === "passengers" ? 700 : 500,
                        padding: "0.5rem 0.25rem",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                    }}
                >
                    <FiUsers size={15} /> Active Bus Passengers ({passengers.filter((p) => p.status === "Active").length})
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("routes")}
                    style={{
                        background: "none",
                        border: "none",
                        borderBottom: activeTab === "routes" ? "2px solid #003b67" : "2px solid transparent",
                        color: activeTab === "routes" ? "#003b67" : "var(--text-muted)",
                        fontWeight: activeTab === "routes" ? 700 : 500,
                        padding: "0.5rem 0.25rem",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                    }}
                >
                    <FiTruck size={15} /> Routes &amp; Distance Slabs ({routes.length})
                </button>
            </div>

            {/* ── TAB 1: PASSENGERS LIST ───────────────────────────────────────── */}
            {activeTab === "passengers" && (
                <>
                    {/* Filters */}
                    <div className="card" style={{ padding: "1rem", marginBottom: "1.5rem" }}>
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ flex: 1, minWidth: "220px", position: "relative" }}>
                                <FiSearch style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                                <input
                                    type="text"
                                    placeholder="Search passenger by student name or reg no..."
                                    className="form-control"
                                    value={searchPassenger}
                                    onChange={(e) => setSearchPassenger(e.target.value)}
                                    style={{ paddingLeft: "32px", fontSize: "13px" }}
                                />
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600 }}>Route:</span>
                                <select
                                    className="form-control"
                                    value={routeFilter}
                                    onChange={(e) => setRouteFilter(e.target.value)}
                                    style={{ fontSize: "13px", padding: "4px 8px" }}
                                >
                                    <option value="ALL">All Routes</option>
                                    {routes.map((r) => (
                                        <option key={r._id} value={r._id}>{r.routeName}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600 }}>Status:</span>
                                <select
                                    className="form-control"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ fontSize: "13px", padding: "4px 8px" }}
                                >
                                    <option value="ALL">All Status</option>
                                    <option value="Active">Active</option>
                                    <option value="Discontinued">Discontinued</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Passengers Table */}
                    <div className="card" style={{ overflow: "hidden" }}>
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: "40px", textAlign: "center" }}>#</th>
                                        <th>Student Details</th>
                                        <th>Route &amp; Bus</th>
                                        <th>Boarding Stop</th>
                                        <th style={{ textAlign: "center" }}>Distance (KM)</th>
                                        <th style={{ textAlign: "right" }}>Monthly Fee</th>
                                        <th style={{ textAlign: "center" }}>Status</th>
                                        <th style={{ textAlign: "right" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingPassengers ? (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                                                Loading passengers...
                                            </td>
                                        </tr>
                                    ) : passengers.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                                                No bus passenger allotments found. Click "Allot Bus Pass" to assign a student.
                                            </td>
                                        </tr>
                                    ) : (
                                        passengers.map((p, idx) => (
                                            <tr key={p._id}>
                                                <td style={{ textAlign: "center", color: "var(--text-muted)" }}>{idx + 1}</td>
                                                <td>
                                                    <div style={{ fontWeight: 700, color: "var(--text)" }}>
                                                        {p.student?.fullName || "—"}
                                                    </div>
                                                    <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                                                        {p.student?.enrollmentNo ? `Reg: ${p.student.enrollmentNo} · ` : ""}
                                                        Yr {p.student?.yearNumber || "1"} {p.student?.mobile ? `· Mob: ${p.student.mobile}` : ""}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{p.route?.routeName || "—"}</div>
                                                    {p.route?.busNumber && (
                                                        <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                                                            Bus No: {p.route.busNumber}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                                        <FiMapPin size={12} color="#003b67" /> {p.stopName}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "center", fontWeight: 700 }}>
                                                    {p.distanceKm} km
                                                    {p.kilometerRange && (
                                                        <div style={{ fontSize: "10.5px", color: "var(--text-muted)", fontWeight: 500 }}>
                                                            {p.kilometerRange}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "right", fontWeight: 800, color: "#16a34a" }}>
                                                    {inr(p.monthlyFee)} <span style={{ fontSize: "11px", fontWeight: 500, color: "var(--text-muted)" }}>/mo</span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {p.status === "Active" ? (
                                                        <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 600 }}>
                                                            Discontinued
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    {p.status === "Active" && (
                                                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setPayPassengerTarget(p);
                                                                    setPassengerMonthsPaid(1);
                                                                    setPassengerPayForm({
                                                                        paymentMode: "Cash",
                                                                        receiptNo: `BUS-${Date.now().toString().slice(-6)}`,
                                                                        transactionRef: "",
                                                                        paymentDate: new Date().toLocaleDateString("en-IN"),
                                                                        remarks: "",
                                                                    });
                                                                }}
                                                                className="btn btn-sm btn-success"
                                                                style={{ padding: "3px 8px", fontSize: "11.5px" }}
                                                                title="Collect Bus Fee"
                                                            >
                                                                💳 Pay
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCancelAllotment(p)}
                                                                className="btn btn-sm btn-outline"
                                                                style={{ padding: "3px 8px", fontSize: "11.5px", color: "#dc2626", borderColor: "#fecaca" }}
                                                                title="Discontinue Bus Facility"
                                                            >
                                                                Discontinue
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* ── TAB 2: ROUTES & DISTANCE SLABS ──────────────────────────────── */}
            {activeTab === "routes" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.25rem" }}>
                    {loadingRoutes ? (
                        <div style={{ padding: "2rem", color: "var(--text-muted)" }}>Loading routes...</div>
                    ) : routes.length === 0 ? (
                        <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", gridColumn: "1 / -1" }}>
                            No bus routes defined yet. Click "Add Bus Route" to configure routes and pricing slabs.
                        </div>
                    ) : (
                        routes.map((r) => (
                            <div key={r._id} className="card" style={{ padding: "1.25rem", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "var(--text)" }}>
                                                {r.routeName}
                                            </h3>
                                            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                                                Bus No: <strong>{r.busNumber || "Not Assigned"}</strong> · Capacity: {r.capacity} seats
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: "0.3rem" }}>
                                            <button
                                                onClick={() => openEditRoute(r)}
                                                className="btn btn-sm btn-outline"
                                                style={{ padding: "3px 6px" }}
                                                title="Edit Route"
                                            >
                                                <FiEdit2 size={13} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteRoute(r)}
                                                className="btn btn-sm btn-outline"
                                                style={{ padding: "3px 6px", color: "#dc2626", borderColor: "#fecaca" }}
                                                title="Delete Route"
                                            >
                                                <FiTrash2 size={13} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Driver info */}
                                    {(r.driverName || r.driverPhone) && (
                                        <div style={{ fontSize: "12px", background: "#f8fafc", padding: "6px 10px", borderRadius: "6px", marginBottom: "0.75rem", display: "flex", gap: "1rem" }}>
                                            <span>Driver: <strong>{r.driverName || "—"}</strong></span>
                                            {r.driverPhone && <span>Ph: <strong>{r.driverPhone}</strong></span>}
                                        </div>
                                    )}

                                    {/* Slabs breakdown */}
                                    <div style={{ marginBottom: "0.75rem" }}>
                                        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "4px" }}>
                                            Distance Slabs &amp; Pricing
                                        </div>
                                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                            {(r.slabs || DEFAULT_SLABS).map((s, idx) => (
                                                <div key={idx} style={{
                                                    background: "#e8f1f8",
                                                    border: "1px solid #b6d5ec",
                                                    borderRadius: "6px",
                                                    padding: "4px 8px",
                                                    fontSize: "11.5px",
                                                }}>
                                                    <span style={{ color: "#1e40af", fontWeight: 600 }}>{s.label}: </span>
                                                    <strong style={{ color: "#16a34a" }}>{inr(s.monthlyFee)}/mo</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Stops preview */}
                                    {r.stops && r.stops.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "4px" }}>
                                                Route Stops ({r.stops.length})
                                            </div>
                                            <div style={{ fontSize: "11.5px", color: "var(--text-muted)", maxHeight: "60px", overflowY: "auto" }}>
                                                {r.stops.map((s) => `${s.stopName} (${s.distanceKm} km)`).join(" → ")}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                        Active Passengers: <strong>{r.passengerCount || 0}</strong> / {r.capacity}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* ── ALLOT BUS PASS MODAL ─────────────────────────────────────────── */}
            {showAllotModal && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "520px", maxHeight: "90vh", overflowY: "auto" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800 }}>🚌 Allot Bus Pass to Student</h3>
                            <button onClick={() => setShowAllotModal(false)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>

                        <form onSubmit={handleAllotSubmit} style={{ padding: "1.25rem" }}>
                            {/* Student Search */}
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Search Student *
                                </label>
                                {selectedStudent ? (
                                    <div style={{
                                        padding: "8px 12px",
                                        background: "#e8f1f8",
                                        border: "1px solid #b6d5ec",
                                        borderRadius: "6px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}>
                                        <div>
                                            <strong>{selectedStudent.fullName}</strong>
                                            <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "8px" }}>
                                                {selectedStudent.enrollmentNo ? `Reg: ${selectedStudent.enrollmentNo}` : ""}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setSelectedStudent(null); setStudentSearch(""); }}
                                            style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700 }}
                                        >
                                            Change
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="text"
                                            placeholder="Search student by name or enrollment no..."
                                            className="form-control"
                                            value={studentSearch}
                                            onChange={(e) => setStudentSearch(e.target.value)}
                                        />
                                        {searchingStudents && <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "3px" }}>Searching...</div>}
                                        {studentsList.length > 0 && (
                                            <div style={{
                                                position: "absolute", top: "100%", left: 0, right: 0,
                                                background: "#fff", border: "1px solid var(--border)",
                                                borderRadius: "6px", maxHeight: "150px", overflowY: "auto",
                                                zIndex: 10, boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                            }}>
                                                {studentsList.map((s) => (
                                                    <div
                                                        key={s._id}
                                                        onClick={() => { setSelectedStudent(s); setStudentsList([]); }}
                                                        style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                                                    >
                                                        <div style={{ fontWeight: 600, fontSize: "13px" }}>{s.fullName}</div>
                                                        <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                                            {s.enrollmentNo ? `Reg: ${s.enrollmentNo} · ` : ""} Yr {s.yearNumber}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Route Selection */}
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Select Route *</label>
                                <select
                                    className="form-control"
                                    value={allotForm.route}
                                    onChange={(e) => {
                                        const routeId = e.target.value;
                                        setAllotForm((prev) => ({ ...prev, route: routeId, stopName: "", distanceKm: "", monthlyFee: "" }));
                                    }}
                                    required
                                >
                                    <option value="">-- Choose Bus Route --</option>
                                    {routes.map((r) => (
                                        <option key={r._id} value={r._id}>{r.routeName} ({r.busNumber || "Bus"})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Predefined Stop Chips */}
                            {allotForm.route && routes.find((r) => r._id === allotForm.route)?.stops?.length > 0 && (
                                <div style={{ marginBottom: "1rem", background: "#f8fafc", padding: "8px 10px", borderRadius: "6px", border: "1px solid var(--border)" }}>
                                    <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
                                        Pick a predefined stop (or type custom below):
                                    </span>
                                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                                        {routes.find((r) => r._id === allotForm.route).stops.map((s, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => {
                                                    setAllotForm((prev) => ({
                                                        ...prev,
                                                        stopName: s.stopName,
                                                        distanceKm: s.distanceKm,
                                                    }));
                                                    handleDistanceChange(s.distanceKm);
                                                }}
                                                style={{
                                                    padding: "3px 8px",
                                                    fontSize: "11px",
                                                    borderRadius: "4px",
                                                    border: allotForm.stopName === s.stopName ? "1.5px solid #003b67" : "1px solid #cbd5e1",
                                                    background: allotForm.stopName === s.stopName ? "#e8f1f8" : "#fff",
                                                    color: allotForm.stopName === s.stopName ? "#002747" : "#334155",
                                                    cursor: "pointer",
                                                    fontWeight: allotForm.stopName === s.stopName ? 700 : 500,
                                                }}
                                            >
                                                {s.stopName} ({s.distanceKm} km)
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Stop and Distance */}
                            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Boarding Stop Name *</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Sitabuldi Square"
                                        className="form-control"
                                        value={allotForm.stopName}
                                        onChange={(e) => handleStopSelected(e.target.value)}
                                        required
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Distance (KM) *</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0.5"
                                        placeholder="e.g. 12"
                                        className="form-control"
                                        value={allotForm.distanceKm}
                                        onChange={(e) => handleDistanceChange(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Auto-calculated Monthly Fee */}
                            <div style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: "6px",
                                padding: "10px 14px",
                                marginBottom: "1rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                                <div>
                                    <span style={{ fontSize: "12px", color: "#15803d", fontWeight: 600 }}>Monthly Bus Fee Payable:</span>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                        {allotForm.distanceKm ? `Based on ${allotForm.distanceKm} KM distance slab` : "Enter distance to calculate"}
                                    </div>
                                </div>
                                <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#15803d" }}>
                                    {inr(allotForm.monthlyFee)} <span style={{ fontSize: "11px", fontWeight: 500 }}>/month</span>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Remarks / Notes (Optional)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Morning batch only, ID pass issued"
                                    className="form-control"
                                    value={allotForm.remarks}
                                    onChange={(e) => setAllotForm({ ...allotForm, remarks: e.target.value })}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                <button type="button" onClick={() => setShowAllotModal(false)} className="btn btn-outline">Cancel</button>
                                <button type="submit" disabled={submittingAllot} className="btn btn-primary">
                                    {submittingAllot ? "Allotting..." : "Confirm Bus Allotment"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── CREATE / EDIT ROUTE MODAL ────────────────────────────────────── */}
            {showRouteModal && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800 }}>
                                {editingRoute ? "Edit Bus Route" : "Add Bus Route & Pricing Slabs"}
                            </h3>
                            <button onClick={() => setShowRouteModal(false)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>

                        <form onSubmit={handleRouteSubmit} style={{ padding: "1.25rem" }}>
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Route Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Route 1 — Sitabuldi to College Campus"
                                    className="form-control"
                                    value={routeForm.routeName}
                                    onChange={(e) => setRouteForm({ ...routeForm, routeName: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Bus Number</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. MH 31 AB 1234"
                                        className="form-control"
                                        value={routeForm.busNumber}
                                        onChange={(e) => setRouteForm({ ...routeForm, busNumber: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Driver Name</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Ramesh Patil"
                                        className="form-control"
                                        value={routeForm.driverName}
                                        onChange={(e) => setRouteForm({ ...routeForm, driverName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Driver Phone</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 9876543210"
                                        className="form-control"
                                        value={routeForm.driverPhone}
                                        onChange={(e) => setRouteForm({ ...routeForm, driverPhone: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Slabs Configuration */}
                            <div style={{ marginBottom: "1.25rem", background: "#f8fafc", padding: "12px", borderRadius: "6px", border: "1px solid var(--border)" }}>
                                <div style={{ fontSize: "12px", fontWeight: 800, color: "var(--text)", marginBottom: "8px", textTransform: "uppercase" }}>
                                    Distance Pricing Slabs (Monthly Rates)
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem" }}>
                                    {(routeForm.slabs || DEFAULT_SLABS).map((s, idx) => (
                                        <div key={idx}>
                                            <label style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: "3px" }}>
                                                {s.label} (₹)
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={s.monthlyFee}
                                                onChange={(e) => updateSlabFee(idx, e.target.value)}
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Stops List */}
                            <div style={{ marginBottom: "1.25rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                    <span style={{ fontSize: "12px", fontWeight: 700 }}>Route Stops &amp; Distances</span>
                                    <button
                                        type="button"
                                        onClick={addStopRow}
                                        className="btn btn-sm btn-outline"
                                        style={{ padding: "2px 8px", fontSize: "11.5px" }}
                                    >
                                        + Add Stop
                                    </button>
                                </div>

                                {routeForm.stops.map((stop, idx) => (
                                    <div key={idx} style={{ display: "flex", gap: "0.5rem", marginBottom: "6px", alignItems: "center" }}>
                                        <input
                                            type="text"
                                            placeholder="Stop Name (e.g. Variety Square)"
                                            className="form-control"
                                            value={stop.stopName}
                                            onChange={(e) => updateStopField(idx, "stopName", e.target.value)}
                                            style={{ flex: 2 }}
                                        />
                                        <input
                                            type="number"
                                            step="0.5"
                                            placeholder="Km"
                                            className="form-control"
                                            value={stop.distanceKm}
                                            onChange={(e) => updateStopField(idx, "distanceKm", e.target.value)}
                                            style={{ width: "80px" }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeStopRow(idx)}
                                            style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer" }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                <button type="button" onClick={() => setShowRouteModal(false)} className="btn btn-outline">Cancel</button>
                                <button type="submit" disabled={submittingRoute} className="btn btn-primary">
                                    {submittingRoute ? "Saving..." : editingRoute ? "Update Route" : "Create Route"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Collect Bus Transport Fee Modal ── */}
            {payPassengerTarget && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "460px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <FiTruck color="#003b67" /> Collect Bus Transport Fee
                            </h3>
                            <button onClick={() => setPayPassengerTarget(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handleCollectPassengerFee} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#166534" }}>
                                    {payPassengerTarget.student?.fullName} {payPassengerTarget.student?.enrollmentNo ? `(${payPassengerTarget.student.enrollmentNo})` : ""}
                                </div>
                                <div style={{ fontSize: "12.5px", color: "#15803d", marginTop: "2px" }}>
                                    Route: {payPassengerTarget.route?.routeName} · Stop: {payPassengerTarget.stopName} · Monthly Rate: <strong>₹{payPassengerTarget.monthlyFee}</strong>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Months to Pay *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={passengerMonthsPaid}
                                        onChange={(e) => setPassengerMonthsPaid(Number(e.target.value))}
                                    >
                                        <option value={1}>1 Month (₹{payPassengerTarget.monthlyFee * 1})</option>
                                        <option value={2}>2 Months (₹{payPassengerTarget.monthlyFee * 2})</option>
                                        <option value={3}>3 Months (₹{payPassengerTarget.monthlyFee * 3})</option>
                                        <option value={6}>6 Months (₹{payPassengerTarget.monthlyFee * 6})</option>
                                        <option value={10}>10 Months (₹{payPassengerTarget.monthlyFee * 10})</option>
                                        <option value={12}>12 Months (₹{payPassengerTarget.monthlyFee * 12})</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Total Amount Due
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={inr(passengerMonthsPaid * payPassengerTarget.monthlyFee)}
                                        disabled
                                        style={{ fontWeight: 700, color: "#16a34a", background: "#f8fafc" }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Payment Mode *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={passengerPayForm.paymentMode}
                                        onChange={(e) => setPassengerPayForm({ ...passengerPayForm, paymentMode: e.target.value })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI / QR</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="NEFT">NEFT / Netbanking</option>
                                        <option value="DD">Demand Draft (DD)</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Receipt / Pass No.
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Auto-generated"
                                        value={passengerPayForm.receiptNo}
                                        onChange={(e) => setPassengerPayForm({ ...passengerPayForm, receiptNo: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Transaction Ref / UTR (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. UPI Ref / Bank Txn ID"
                                    value={passengerPayForm.transactionRef}
                                    onChange={(e) => setPassengerPayForm({ ...passengerPayForm, transactionRef: e.target.value })}
                                />
                            </div>

                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn-outline" onClick={() => setPayPassengerTarget(null)}>Cancel</button>
                                <button type="submit" className="btn btn-success" disabled={submittingPassengerPay}>
                                    {submittingPassengerPay ? "Processing…" : `Confirm ${inr(passengerMonthsPaid * payPassengerTarget.monthlyFee)} Bus Payment`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
